/* Custom local storage hook that is non-reactive (unlike useLocalStorage from usehooks-ts), as in some
   cases it is not desired to re-render the component (or hook) when the local storage value changes. */

import { useCallback } from "react";

type UseNonReactiveLocalStorage<T> = [
  () => T | null,
  (value: T) => void,
  () => void,
];

export const useNonReactiveLocalStorage = <T>(
  key: string,
  initialValue: T | null = null,
): UseNonReactiveLocalStorage<T> => {
  const getValue = useCallback(() => {
    const value = localStorage.getItem(key);

    if (value !== null) {
      return JSON.parse(value);
    }

    return initialValue;
  }, [key, initialValue]);

  const setValue = useCallback(
    (value: T) => {
      localStorage.setItem(key, JSON.stringify(value));
    },
    [key],
  );

  const removeValue = useCallback(() => {
    localStorage.removeItem(key);
  }, [key]);

  return [getValue, setValue, removeValue];
};
